import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from './config';
import axios from 'axios';
import logo from './assets/logo.svg';

const fallbackNodeTypes = [
  'aggregate', 'ai_agent', 'ai_summarizer', 'airtable', 'airtable_trigger', 'anthropic', 'asana', 'aws_s3',
  'azure_blob_storage', 'bigquery', 'browser_automation', 'calendly', 'call_workflow', 'chat_trigger',
  'cicd_trigger', 'clickup', 'code', 'compare_datasets', 'confluence', 'convertkit', 'csv',
  'currency_converter', 'data_validation', 'database_change_trigger', 'databricks', 'datastore', 'datetime',
  'debug', 'deduplicate', 'discord', 'docker', 'document_intelligence', 'dynamic', 'dynamics365', 'dynamodb',
  'elasticsearch', 'embeddings', 'excel', 'exchange_rate', /* Note: 'excel' is the node type; credential service_type is 'microsoft' */ 'file_to_base64', 'firebase', 'form_input',
  'form_trigger', 'freshdesk', 'gemini', 'github', 'github_trigger', 'gitlab', 'gmail', 'gmail_trigger',
  'google_calendar', 'google_cloud_storage', 'google_docs', 'google_sheets', 'google_sheets_trigger',
  'hashicorp_vault', 'holiday_checker', 'http', 'hubspot', 'hubspot_trigger', 'human_approval', 'if',
  'image_generation', 'instagram', 'intercom', 'ip_geolocation', 'jira', 'jira_trigger', 'json_processor',
  'kafka', 'kubernetes', 'linear', 'linear_trigger', 'linkedin', 'loop', 'mailchimp', 'maintenance', 'merge',
  'monday', 'mongodb', 'mqtt', 'mysql', 'neon', 'notion', 'notion_trigger', 'object_storage', 'ocr', 'ollama',
  'openai', 'outlook_calendar', 'outlook', 'outlook_trigger', 'pagerduty', 'paginate', 'parallel_branches',
  'pdf_processor', 'perplexity', 'pii_detector', 'pipedrive', 'postgres', 'qr_code_generator', 'queue_bridge',
  'quickbooks', 'redis', 'rss_feed_trigger', 's3_trigger', 'salesforce', 'screenshot', 'secrets_manager',
  'send_mail', 'sendgrid', 'servicenow', 'set_variables', 'sftp_trigger', 'sharepoint', 'shopify',
  'shopify_trigger', 'slack', 'slack_trigger', 'snowflake', 'sort', 'speech_to_text', 'split_in_batches',
  'sqlserver', 'ssh', 'stop_and_error', 'stripe', 'stripe_trigger', 'supabase', 'switch', 'table',
  'table_trigger', 'teams', 'telegram', 'text_manipulator', 'text_to_speech', 'throttle', 'transform', 'trello',
  'try_catch', 'twilio', 'twitter', 'url_shortener', 'vector_store', 'virus_scan', 'wait', 'weather',
  'web_scraper', 'webhook', 'webhook_poller', 'webhook_response', 'websocket_client', 'woocommerce', 'xero',
  'xml', 'zendesk', 'zoho_crm', 'zoom'
];

const featureSections = [
  {
    id: 'start',
    title: 'Getting Started',
    bullets: [
      'Create your first workflow from template, AI generation, import, or blank canvas.',
      'Add nodes from the palette or quick launcher (Space, /, Ctrl/Cmd + K).',
      'Configure credentials once in Credential Manager and reuse securely.',
      'Run manually, schedule with cron, or publish webhook/form/chat triggers.'
    ]
  },
  {
    id: 'builder',
    title: 'Workflow Builder',
    bullets: [
      'Drag-and-drop canvas with branching, loops, switch, merge, and error handling.',
      'Expression editor supports autocomplete for node outputs and env variables.',
      'Pin test data on any node for isolated downstream debugging.',
      'Real-time node overlay shows running, success, and failure states.'
    ]
  },
  {
    id: 'execution',
    title: 'Execution & Operations',
    bullets: [
      'Queue-backed asynchronous execution with run history and node-level output inspection.',
      'Step-through replay and node re-execution for incident recovery.',
      'Metrics dashboard tracks success rate, latency, and trend lines.',
      'Alert rules trigger via email, Slack, and webhooks.'
    ]
  },
  {
    id: 'governance',
    title: 'Security & Governance',
    bullets: [
      'Credentials are encrypted at rest and resolved only at runtime.',
      'Workspace RBAC with admin/editor/viewer roles.',
      'Audit logs capture create/update/delete/run/generate events.',
      'AI governance supports provider allow-lists and approval gates.'
    ]
  }
];

const workflowExamples = [
  {
    title: 'Lead Intake → CRM + Slack',
    steps: ['Form Trigger', 'Data Validation', 'HubSpot/Salesforce node', 'Slack node', 'Send Mail node'],
    outcome: 'Every new form submission is validated, pushed to CRM, and announced in Slack with email confirmation.'
  },
  {
    title: 'Support Triage with AI',
    steps: ['Gmail/Outlook Trigger', 'AI Agent', 'Jira node', 'Human Approval', 'Zendesk/Freshdesk node'],
    outcome: 'Incoming support emails are summarized, prioritized, and routed with optional human approval.'
  },
  {
    title: 'Data Sync Pipeline',
    steps: ['Schedule Trigger', 'Database node', 'Compare Datasets', 'BigQuery/Snowflake', 'Monitoring alert'],
    outcome: 'Scheduled ETL syncs data warehouses and raises alerts when sync quality degrades.'
  }
];

const inferCategory = (type) => {
  if (type.endsWith('_trigger') || ['webhook', 'chat_trigger', 'form_trigger'].includes(type)) return 'Triggers';
  if (['openai', 'anthropic', 'gemini', 'ollama', 'embeddings', 'vector_store', 'ai_agent', 'ai_summarizer', 'image_generation', 'speech_to_text', 'text_to_speech', 'perplexity'].includes(type)) return 'AI';
  if (['if', 'switch', 'loop', 'merge', 'wait', 'try_catch', 'parallel_branches', 'throttle', 'split_in_batches', 'stop_and_error', 'set_variables'].includes(type)) return 'Flow Control';
  if (['postgres', 'mysql', 'sqlserver', 'mongodb', 'redis', 'snowflake', 'bigquery', 'databricks', 'supabase', 'table', 'table_trigger', 'neon'].includes(type)) return 'Data & Storage';
  if (['http', 'webhook_response', 'websocket_client', 'webhook_poller', 'web_scraper', 'browser_automation', 'screenshot'].includes(type)) return 'Web & APIs';
  if (['slack', 'teams', 'discord', 'send_mail', 'sendgrid', 'telegram', 'twilio', 'gmail', 'outlook', 'outlook_calendar', 'google_calendar'].includes(type)) return 'Messaging';
  if (['jira', 'confluence', 'notion', 'trello', 'monday', 'asana', 'clickup', 'linear', 'github', 'gitlab'].includes(type)) return 'Collaboration';
  if (['salesforce', 'hubspot', 'zoho_crm', 'pipedrive', 'dynamics365', 'intercom', 'freshdesk', 'servicenow', 'zendesk', 'shopify', 'woocommerce', 'stripe', 'quickbooks', 'xero'].includes(type)) return 'Business Apps';
  return 'Utilities';
};

const toTitle = (type) => type.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');

const buildFallbackDetails = (type) => {
  const isTrigger = type.endsWith('_trigger') || ['webhook', 'chat_trigger', 'form_trigger'].includes(type);
  if (isTrigger) {
    return {
      summary: `Starts a workflow when a ${toTitle(type).toLowerCase()} event arrives.`,
      useCase: 'Use this node at the beginning of workflows to ingest external events.',
      inputs: ['Source-specific configuration', 'Optional filters', 'Credential (if required)'],
      example: `{ "node": "${type}", "mode": "trigger", "enabled": true }`
    };
  }
  return {
    summary: `Performs ${toTitle(type).toLowerCase()} actions as part of a workflow.`,
    useCase: 'Use this node to transform data, call external systems, or enrich run context.',
    inputs: ['action', 'credential_id (if required)', 'payload/query fields'],
    example: `{ "node": "${type}", "action": "default", "input": "{{ trigger.body }}" }`
  };
};

const categoryStyle = {
  Triggers: { background: '#fee2e2', color: '#991b1b' },
  AI: { background: '#ede9fe', color: '#5b21b6' },
  'Flow Control': { background: '#dbeafe', color: '#1e3a8a' },
  'Data & Storage': { background: '#dcfce7', color: '#14532d' },
  'Web & APIs': { background: '#cffafe', color: '#155e75' },
  Messaging: { background: '#fce7f3', color: '#9d174d' },
  Collaboration: { background: '#fef3c7', color: '#92400e' },
  'Business Apps': { background: '#fae8ff', color: '#86198f' },
  Utilities: { background: '#e5e7eb', color: '#374151' }
};

const Docs = ({ onBack }) => {
    const [nodes, setNodes] = useState([]);
    const [loadingNodes, setLoadingNodes] = useState(true);
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'system');
    const [nodeSearch, setNodeSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [nodeMetaSource, setNodeMetaSource] = useState('fallback');

    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    setTheme(document.documentElement.getAttribute('data-theme') || 'light');
                }
            });
        });
        observer.observe(document.documentElement, { attributes: true });
        setTheme(document.documentElement.getAttribute('data-theme') || 'light');
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
      const loadNodes = async () => {
        try {
          const token = localStorage.getItem('token');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const res = await axios.get(`${API_BASE_URL}/nodes`, { headers });
          const apiNodes = Array.isArray(res.data) ? res.data : [];
          if (apiNodes.length > 0) {
            setNodes(apiNodes.map((n) => ({
              type: n.type,
              label: n.label || toTitle(n.type),
              category: n.category || inferCategory(n.type),
              description: n.description || '',
              fields: Array.isArray(n.fields) ? n.fields : []
            })));
            setNodeMetaSource('api');
            setLoadingNodes(false);
            return;
          }
        } catch (e) {
          setNodeMetaSource('fallback');
        }
        setNodes(
          fallbackNodeTypes.map((type) => ({
            type,
            label: toTitle(type),
            category: inferCategory(type),
            description: '',
            fields: []
          }))
        );
        setLoadingNodes(false);
      };
      loadNodes();
    }, []);

    const categories = useMemo(() => {
      const set = new Set(nodes.map((n) => n.category || inferCategory(n.type)));
      return ['All', ...Array.from(set).sort()];
    }, [nodes]);

    const filteredNodes = useMemo(() => {
      return nodes
        .filter((node) => categoryFilter === 'All' || (node.category || inferCategory(node.type)) === categoryFilter)
        .filter((node) => {
          const q = nodeSearch.trim().toLowerCase();
          if (!q) return true;
          const haystack = `${node.label} ${node.type} ${node.description}`.toLowerCase();
          return haystack.includes(q);
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [nodes, categoryFilter, nodeSearch]);

    const buildNodeExample = (node) => {
      if (node.fields && node.fields.length) {
        const example = {};
        node.fields.slice(0, 5).forEach((field) => {
          if (!field?.name) return;
          if (field.default !== undefined) {
            example[field.name] = field.default;
            return;
          }
          if (field.type === 'number') example[field.name] = 1;
          else if (field.type === 'boolean') example[field.name] = true;
          else if (field.type === 'select' && Array.isArray(field.options) && field.options.length) example[field.name] = field.options[0];
          else if (field.type === 'credential') example[field.name] = '{{REQUIRED:credential_id}}';
          else example[field.name] = `{{REQUIRED:${field.name}}}`;
        });
        return JSON.stringify(example, null, 2);
      }
      return buildFallbackDetails(node.type).example;
    };

    const renderNodeInputs = (node) => {
      if (node.fields && node.fields.length) {
        return node.fields.slice(0, 6).map((field) => field.label || field.name);
      }
      return buildFallbackDetails(node.type).inputs;
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100vw',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            overflow: 'hidden'
        }}>
            <header style={{
                height: '64px',
                padding: '0 40px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                flexShrink: 0,
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={onBack}>
                    <img src={logo} alt="xavvy" style={{ height: '28px' }} />
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px', color: 'var(--text-primary)' }}>xavvy Product Documentation</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <a
                    href="#nodes"
                    style={{
                      color: 'var(--accent-color)',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}
                  >
                    Node Catalog
                  </a>
                  <button
                      onClick={onBack}
                      style={{
                          padding: '8px 20px',
                          borderRadius: '6px',
                          background: 'var(--accent-color)',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          transition: 'opacity 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.opacity = '0.9'}
                      onMouseOut={(e) => e.target.style.opacity = '1'}
                  >
                      Back to Home
                  </button>
                </div>
            </header>

            <div style={{
                flex: 1,
                overflow: 'hidden',
                display: 'grid',
                gridTemplateColumns: '280px 1fr',
                background: 'var(--bg-primary)'
            }}>
                <aside style={{
                  borderRight: '1px solid var(--border-color)',
                  padding: '24px 18px',
                  overflowY: 'auto',
                  background: 'var(--bg-secondary)'
                }}>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '10px', fontWeight: 700 }}>Navigate</div>
                  {[
                    ['overview', 'Overview'],
                    ['quickstart', 'Quick Start'],
                    ['features', 'Features'],
                    ['examples', 'Workflow Examples'],
                    ['screenshots', 'Screenshots'],
                    ['nodes', 'All Nodes']
                  ].map(([id, label]) => (
                    <a key={id} href={`#${id}`} style={{ display: 'block', color: 'var(--text-primary)', textDecoration: 'none', padding: '8px 10px', borderRadius: '6px', marginBottom: '4px' }}>
                      {label}
                    </a>
                  ))}
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '16px 0 8px', fontWeight: 700 }}>Docs Mode</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                    {nodeMetaSource === 'api'
                      ? 'Node details are loaded live from backend metadata.'
                      : 'Node catalog is rendered from built-in fallback list for public access.'}
                  </div>
                  <div style={{ marginTop: '16px', fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
                    Theme: {theme}
                  </div>
                </aside>

                <main style={{ overflowY: 'auto', padding: '32px 38px' }}>
                  <section id="overview" style={{ marginBottom: '34px' }}>
                    <h1 style={{ marginTop: 0, marginBottom: '12px' }}>xavvy End-User Documentation</h1>
                    <p style={{ margin: 0, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      This guide helps business users, automation builders, and technical teams design, run, monitor, and maintain workflows in xavvy. It is structured for fast onboarding and deep operational usage.
                    </p>
                    <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ padding: '6px 10px', borderRadius: '999px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>Visual Builder</span>
                      <span style={{ padding: '6px 10px', borderRadius: '999px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>AI Agent</span>
                      <span style={{ padding: '6px 10px', borderRadius: '999px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>100+ Nodes</span>
                      <span style={{ padding: '6px 10px', borderRadius: '999px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>Enterprise Controls</span>
                    </div>
                  </section>

                  <section id="quickstart" style={{ marginBottom: '34px' }}>
                    <h2 style={{ marginBottom: '8px' }}>Quick Start</h2>
                    <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                      <li>Open <strong>+ New Workflow</strong> and pick a template or start blank.</li>
                      <li>Add a trigger node first, then action and logic nodes.</li>
                      <li>Configure credentials in <strong>Credential Manager</strong> and select them in nodes.</li>
                      <li>Run the workflow manually and inspect per-node outputs.</li>
                      <li>Publish with schedule, webhook, form, or chat trigger.</li>
                    </ol>
                  </section>

                  <section id="features" style={{ marginBottom: '34px' }}>
                    <h2 style={{ marginBottom: '10px' }}>Feature Guide</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
                      {featureSections.map((section) => (
                        <div key={section.id} style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '14px', background: 'var(--bg-secondary)' }}>
                          <div style={{ fontWeight: 700, marginBottom: '8px' }}>{section.title}</div>
                          <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            {section.bullets.map((b) => <li key={b}>{b}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section id="examples" style={{ marginBottom: '34px' }}>
                    <h2 style={{ marginBottom: '10px' }}>Workflow Examples</h2>
                    <div style={{ display: 'grid', gap: '14px' }}>
                      {workflowExamples.map((example) => (
                        <div key={example.title} style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '14px', background: 'var(--bg-secondary)' }}>
                          <div style={{ fontWeight: 700, marginBottom: '8px' }}>{example.title}</div>
                          <div style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                            {example.steps.join(' → ')}
                          </div>
                          <div style={{ fontSize: '0.92rem', color: 'var(--text-primary)' }}>{example.outcome}</div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section id="screenshots" style={{ marginBottom: '34px' }}>
                    <h2 style={{ marginBottom: '10px' }}>Product Screenshots</h2>
                    <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
                      These visuals show the key product surfaces for quick orientation.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                      {[
                        ['/docs/screenshots/canvas-workflow.svg', 'Workflow Canvas'],
                        ['/docs/screenshots/node-config.svg', 'Node Configuration Panel'],
                        ['/docs/screenshots/run-history.svg', 'Run History & Debugger']
                      ].map(([src, title]) => (
                        <figure key={src} style={{ margin: 0, border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden', background: 'var(--bg-secondary)' }}>
                          <img src={src} alt={title} style={{ width: '100%', display: 'block' }} />
                          <figcaption style={{ padding: '10px 12px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{title}</figcaption>
                        </figure>
                      ))}
                    </div>
                  </section>

                  <section id="nodes" style={{ marginBottom: '40px' }}>
                    <h2 style={{ marginBottom: '10px' }}>Complete Node Catalog</h2>
                    <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
                      Browse all nodes, categories, key parameters, and usage examples. This section is searchable and designed for day-to-day implementation.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', marginBottom: '12px' }}>
                      <input
                        type="text"
                        value={nodeSearch}
                        onChange={(e) => setNodeSearch(e.target.value)}
                        placeholder="Search nodes by name, type, or description..."
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-secondary)',
                          color: 'var(--text-primary)'
                        }}
                      />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {categories.map((category) => (
                          <button
                            key={category}
                            onClick={() => setCategoryFilter(category)}
                            style={{
                              border: '1px solid var(--border-color)',
                              padding: '6px 10px',
                              borderRadius: '999px',
                              cursor: 'pointer',
                              background: categoryFilter === category ? 'var(--accent-color)' : 'var(--bg-secondary)',
                              color: categoryFilter === category ? '#fff' : 'var(--text-primary)',
                              fontSize: '0.85rem'
                            }}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    </div>

                    {loadingNodes ? (
                      <div style={{ color: 'var(--text-secondary)', padding: '12px 0' }}>Loading node catalog...</div>
                    ) : (
                      <div style={{ display: 'grid', gap: '10px' }}>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                          Showing {filteredNodes.length} of {nodes.length} nodes
                        </div>
                        {filteredNodes.map((node) => {
                          const details = buildFallbackDetails(node.type);
                          const inputs = renderNodeInputs(node);
                          const category = node.category || inferCategory(node.type);
                          const color = categoryStyle[category] || categoryStyle.Utilities;
                          return (
                            <details key={node.type} style={{ border: '1px solid var(--border-color)', borderRadius: '10px', background: 'var(--bg-secondary)' }}>
                              <summary style={{ listStyle: 'none', cursor: 'pointer', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                <div>
                                  <div style={{ fontWeight: 700 }}>{node.label}</div>
                                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{node.type}</div>
                                </div>
                                <span style={{ padding: '4px 8px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, background: color.background, color: color.color }}>
                                  {category}
                                </span>
                              </summary>
                              <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--border-color)' }}>
                                <p style={{ marginTop: '10px', color: 'var(--text-secondary)' }}>
                                  {node.description || details.summary}
                                </p>
                                <div style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
                                  <strong>When to use:</strong> {details.useCase}
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                  <strong style={{ fontSize: '0.9rem' }}>Key inputs:</strong>
                                  <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                                    {inputs.map((input) => <li key={`${node.type}-${input}`}>{input}</li>)}
                                  </ul>
                                </div>
                                <div style={{ marginTop: '10px' }}>
                                  <strong style={{ fontSize: '0.9rem' }}>Example configuration:</strong>
                                  <pre style={{
                                    marginTop: '6px',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    background: 'var(--bg-primary)',
                                    border: '1px solid var(--border-color)',
                                    overflowX: 'auto',
                                    fontSize: '0.82rem'
                                  }}>
{buildNodeExample(node)}
                                  </pre>
                                </div>
                              </div>
                            </details>
                          );
                        })}
                      </div>
                    )}
                  </section>
                </main>
            </div>
        </div>
    );
};

export default Docs;
